import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger 
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  User, 
  FileText, 
  Briefcase, 
  CreditCard, 
  Lock, 
  Building,
  Calendar,
  CheckCircle,
  XCircle,
  Clock,
  IdCard,
  Wallet,
  GraduationCap,
  Shield,
  ArrowUpCircle,
  ArrowDownCircle,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  UserPlus,
  UserMinus,
  History
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATO_BADGE = {
  ATTIVO: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
  SCADUTO: { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  REVOCATO: { color: 'bg-red-100 text-red-800', icon: XCircle },
  INVIATA: { color: 'bg-blue-100 text-blue-800', icon: Clock },
  IN_REVISIONE: { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  ACCETTATA: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
  RIFIUTATA: { color: 'bg-red-100 text-red-800', icon: XCircle },
  RICEVUTO: { color: 'bg-blue-100 text-blue-800', icon: Clock },
  APPROVATO: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
  DA_PAGARE: { color: 'bg-red-100 text-red-800', icon: AlertTriangle },
  PAGATA: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
};

const Profilo = () => {
  const { user } = useAuth();
  const [riepilogo, setRiepilogo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordData, setPasswordData] = useState({
    old_password: '',
    new_password: '',
    confirm_password: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API_URL}/cittadino/riepilogo`);
      setRiepilogo(res.data);
    } catch (error) {
      console.error('Error fetching profile data:', error);
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Le password non coincidono');
      return;
    }
    if (passwordData.new_password.length < 6) {
      toast.error('La nuova password deve essere di almeno 6 caratteri');
      return;
    }
    try {
      await axios.post(`${API_URL}/auth/change-password`, {
        old_password: passwordData.old_password,
        new_password: passwordData.new_password
      });
      toast.success('Password aggiornata con successo');
      setPasswordDialogOpen(false);
      setPasswordData({ old_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel cambio password');
    }
  };

  const getStatusBadge = (stato) => {
    const config = STATO_BADGE[stato] || { color: 'bg-slate-100 text-slate-800', icon: Clock };
    const Icon = config.icon;
    return (
      <Badge className={config.color}>
        <Icon className="w-3 h-3 mr-1" />
        {stato?.replace('_', ' ')}
      </Badge>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="profilo-page">
      {/* Header con info utente */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-lg p-6 text-white">
        <div className="flex items-center gap-4">
          <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center">
            <span className="text-3xl font-bold">
              {user?.nome?.[0]}{user?.cognome?.[0]}
            </span>
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-bold">{user?.nome} {user?.cognome}</h1>
            <p className="text-slate-300">@{user?.username}</p>
            <div className="flex items-center gap-2 mt-2">
              <Badge className="bg-white/20 text-white">
                {user?.ruolo_azienda_nome || user?.ruolo}
              </Badge>
              {user?.azienda_nome && (
                <Badge className="bg-amber-500/20 text-amber-200">
                  <Building className="w-3 h-3 mr-1" />
                  {user.azienda_nome}
                </Badge>
              )}
            </div>
          </div>
          <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="bg-white/10 border-white/20 text-white hover:bg-white/20">
                <Lock className="w-4 h-4 mr-2" />
                Cambia Password
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Cambia Password</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleChangePassword} className="space-y-4">
                <div className="space-y-2">
                  <Label>Password Attuale</Label>
                  <Input
                    type="password"
                    value={passwordData.old_password}
                    onChange={(e) => setPasswordData({...passwordData, old_password: e.target.value})}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nuova Password</Label>
                  <Input
                    type="password"
                    value={passwordData.new_password}
                    onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Conferma Nuova Password</Label>
                  <Input
                    type="password"
                    value={passwordData.confirm_password}
                    onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
                    required
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setPasswordDialogOpen(false)}>
                    Annulla
                  </Button>
                  <Button type="submit">Aggiorna Password</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Cards Riepilogo */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Saldo Totale */}
        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-100">Saldo Totale</p>
                <p className="text-2xl font-bold">{formatCurrency(riepilogo?.finanze?.saldo_totale)}</p>
                <p className="text-xs text-green-200">{riepilogo?.finanze?.numero_conti || 0} conti</p>
              </div>
              <Wallet className="w-10 h-10 text-green-200" />
            </div>
          </CardContent>
        </Card>

        {/* Media Voti */}
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-100">Media Voti</p>
                <p className="text-2xl font-bold">{riepilogo?.istruzione?.media_voti || '-'}</p>
                <p className="text-xs text-blue-200">{riepilogo?.istruzione?.numero_voti || 0} voti</p>
              </div>
              <GraduationCap className="w-10 h-10 text-blue-200" />
            </div>
          </CardContent>
        </Card>

        {/* Documenti */}
        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-100">Documenti</p>
                <p className="text-2xl font-bold">{riepilogo?.documenti?.totale || 0}</p>
                <p className="text-xs text-purple-200">CDI, Tessere</p>
              </div>
              <IdCard className="w-10 h-10 text-purple-200" />
            </div>
          </CardContent>
        </Card>

        {/* Fedina Penale */}
        <Card className={`bg-gradient-to-br ${(riepilogo?.fedina_penale?.multe_non_pagate || 0) > 0 ? 'from-red-500 to-red-600' : 'from-slate-500 to-slate-600'} text-white`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-white/80">Fedina Penale</p>
                <p className="text-2xl font-bold">{riepilogo?.fedina_penale?.multe_totali || 0} multe</p>
                {(riepilogo?.fedina_penale?.multe_non_pagate || 0) > 0 && (
                  <p className="text-xs text-red-200">{riepilogo?.fedina_penale?.multe_non_pagate} da pagare</p>
                )}
              </div>
              <Shield className="w-10 h-10 text-white/60" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs per le diverse sezioni */}
      <Tabs defaultValue="finanze" className="w-full">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="finanze" className="gap-2">
            <Wallet className="w-4 h-4" />
            Finanze
          </TabsTrigger>
          <TabsTrigger value="voti" className="gap-2">
            <GraduationCap className="w-4 h-4" />
            Voti
          </TabsTrigger>
          <TabsTrigger value="documenti" className="gap-2">
            <IdCard className="w-4 h-4" />
            Documenti
          </TabsTrigger>
          <TabsTrigger value="fedina" className="gap-2">
            <Shield className="w-4 h-4" />
            Fedina Penale
          </TabsTrigger>
          <TabsTrigger value="lavoro" className="gap-2">
            <Briefcase className="w-4 h-4" />
            Candidature
          </TabsTrigger>
          <TabsTrigger value="assunzioni" className="gap-2">
            <History className="w-4 h-4" />
            Assunzioni
          </TabsTrigger>
        </TabsList>

        {/* FINANZE */}
        <TabsContent value="finanze" className="mt-6 space-y-4">
          {/* Conti */}
          <div>
            <h3 className="text-lg font-semibold mb-3">I Miei Conti</h3>
            {(riepilogo?.finanze?.conti?.length || 0) === 0 ? (
              <Card className="border-dashed">
                <CardContent className="flex flex-col items-center justify-center py-8">
                  <CreditCard className="w-10 h-10 text-slate-300 mb-2" />
                  <p className="text-slate-500">Nessun conto bancario</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {riepilogo?.finanze?.conti?.map((conto) => (
                  <Card key={conto.id}>
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <Badge variant="outline">{conto.tipo_conto}</Badge>
                          <p className="font-mono text-sm text-slate-500 mt-1">{conto.iban}</p>
                        </div>
                        <div className="text-right">
                          <p className={`text-xl font-bold ${conto.saldo >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatCurrency(conto.saldo)}
                          </p>
                          {conto.carta_attiva && (
                            <Badge className="bg-purple-100 text-purple-800 mt-1">
                              <CreditCard className="w-3 h-3 mr-1" />
                              Carta Attiva
                            </Badge>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* Ultime Transazioni */}
          <div>
            <h3 className="text-lg font-semibold mb-3">Ultime Transazioni</h3>
            {(riepilogo?.finanze?.ultime_transazioni?.length || 0) === 0 ? (
              <Card className="border-dashed">
                <CardContent className="flex flex-col items-center justify-center py-8">
                  <TrendingUp className="w-10 h-10 text-slate-300 mb-2" />
                  <p className="text-slate-500">Nessuna transazione</p>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="p-0">
                  <div className="divide-y">
                    {riepilogo?.finanze?.ultime_transazioni?.map((trans, idx) => (
                      <div key={idx} className="flex items-center justify-between p-4">
                        <div className="flex items-center gap-3">
                          {trans.tipo === 'DEPOSITO' ? (
                            <ArrowDownCircle className="w-8 h-8 text-green-500" />
                          ) : (
                            <ArrowUpCircle className="w-8 h-8 text-red-500" />
                          )}
                          <div>
                            <p className="font-medium">{trans.causale}</p>
                            <p className="text-xs text-slate-500">
                              {new Date(trans.created_at).toLocaleDateString('it-IT')} • {trans.tipo_conto}
                            </p>
                          </div>
                        </div>
                        <span className={`font-bold ${trans.tipo === 'DEPOSITO' ? 'text-green-600' : 'text-red-600'}`}>
                          {trans.tipo === 'DEPOSITO' ? '+' : '-'}{formatCurrency(trans.importo)}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* VOTI */}
        <TabsContent value="voti" className="mt-6">
          {(riepilogo?.istruzione?.ultimi_voti?.length || 0) === 0 ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <GraduationCap className="w-12 h-12 text-slate-300 mb-4" />
                <p className="text-slate-500">Nessun voto registrato</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              <Card className="bg-blue-50">
                <CardContent className="p-4">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Media Generale</span>
                    <span className={`text-2xl font-bold ${(riepilogo?.istruzione?.media_voti || 0) >= 6 ? 'text-green-600' : 'text-red-600'}`}>
                      {riepilogo?.istruzione?.media_voti}
                    </span>
                  </div>
                </CardContent>
              </Card>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {riepilogo?.istruzione?.ultimi_voti?.map((voto) => (
                  <Card key={voto.id}>
                    <CardHeader className="pb-2">
                      <div className="flex justify-between items-start">
                        <div>
                          <CardTitle className="text-base">{voto.materia}</CardTitle>
                          <CardDescription>{voto.descrizione}</CardDescription>
                        </div>
                        <Badge className={voto.voto >= 6 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                          {voto.voto}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-xs text-slate-400">
                        {new Date(voto.data_voto).toLocaleDateString('it-IT')} • {voto.docente_nome}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        {/* DOCUMENTI */}
        <TabsContent value="documenti" className="mt-6">
          {(riepilogo?.documenti?.lista?.length || 0) === 0 ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <IdCard className="w-12 h-12 text-slate-300 mb-4" />
                <p className="text-slate-500">Nessun documento</p>
                <p className="text-sm text-slate-400">I tuoi documenti ufficiali appariranno qui</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {riepilogo?.documenti?.lista?.map((doc) => (
                <Card key={doc.id} className="hover:shadow-md transition-shadow">
                  <CardHeader className="pb-2">
                    <div className="flex justify-between items-start">
                      <div className="flex items-center gap-2">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                          <IdCard className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <CardTitle className="text-base">{doc.titolo}</CardTitle>
                          <CardDescription>{doc.tipo}</CardDescription>
                        </div>
                      </div>
                      {getStatusBadge(doc.stato)}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-slate-600">{doc.contenuto}</p>
                    <p className="text-xs text-slate-400 mt-2">
                      Rilasciato: {new Date(doc.created_at).toLocaleDateString('it-IT')}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* FEDINA PENALE */}
        <TabsContent value="fedina" className="mt-6 space-y-4">
          {/* Riepilogo Fedina */}
          <Card className={`${(riepilogo?.fedina_penale?.multe_non_pagate || 0) > 0 ? 'border-red-200 bg-red-50' : 'bg-green-50 border-green-200'}`}>
            <CardContent className="p-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Multe Totali</p>
                  <p className="text-2xl font-bold">{riepilogo?.fedina_penale?.multe_totali || 0}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Non Pagate</p>
                  <p className={`text-2xl font-bold ${(riepilogo?.fedina_penale?.multe_non_pagate || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {riepilogo?.fedina_penale?.multe_non_pagate || 0}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Importo Totale</p>
                  <p className="text-lg font-medium">{formatCurrency(riepilogo?.fedina_penale?.importo_totale_multe)}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Da Pagare</p>
                  <p className={`text-lg font-bold ${(riepilogo?.fedina_penale?.importo_da_pagare || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {formatCurrency(riepilogo?.fedina_penale?.importo_da_pagare)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Note sulla Fedina */}
          {(riepilogo?.fedina_penale?.note?.length || 0) > 0 && (
            <div>
              <h3 className="text-lg font-semibold mb-3">Note sulla Fedina</h3>
              <div className="space-y-2">
                {riepilogo?.fedina_penale?.note?.map((nota, idx) => (
                  <Card key={idx}>
                    <CardContent className="p-3">
                      <div className="flex justify-between items-start">
                        <div>
                          <Badge className={
                            nota.tipo === 'NOTA_POSITIVA' ? 'bg-green-100 text-green-800' :
                            nota.tipo === 'RIABILITAZIONE' ? 'bg-blue-100 text-blue-800' :
                            'bg-yellow-100 text-yellow-800'
                          }>
                            {nota.tipo}
                          </Badge>
                          <p className="text-sm mt-1">{nota.descrizione}</p>
                          <p className="text-xs text-slate-400">{nota.agente_nome} - {new Date(nota.created_at).toLocaleDateString('it-IT')}</p>
                        </div>
                        <Badge variant="outline">Gravità: {nota.gravita}/5</Badge>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Storico Multe */}
          <div>
            <h3 className="text-lg font-semibold mb-3">Storico Multe</h3>
            {(riepilogo?.fedina_penale?.storico_multe?.length || 0) === 0 ? (
              <Card className="border-dashed border-green-200 bg-green-50">
                <CardContent className="flex flex-col items-center justify-center py-8">
                  <CheckCircle className="w-10 h-10 text-green-400 mb-2" />
                  <p className="text-green-600 font-medium">Fedina Pulita</p>
                  <p className="text-sm text-green-500">Nessuna multa registrata</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {riepilogo?.fedina_penale?.storico_multe?.map((multa) => (
                  <Card key={multa.id}>
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium">{multa.motivo}</p>
                          <p className="text-sm text-slate-500">{multa.luogo}</p>
                          <p className="text-xs text-slate-400 mt-1">
                            {new Date(multa.data_infrazione).toLocaleDateString('it-IT')} • Agente: {multa.agente_nome}
                          </p>
                        </div>
                        <div className="text-right">
                          {getStatusBadge(multa.stato)}
                          <p className="text-lg font-bold mt-1">{formatCurrency(multa.importo)}</p>
                          {multa.stato === 'PAGATA' && (
                            <p className="text-xs text-green-600">
                              Pagata il {new Date(multa.data_pagamento).toLocaleDateString('it-IT')}
                            </p>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* CANDIDATURE */}
        <TabsContent value="lavoro" className="mt-6">
          {(riepilogo?.lavoro?.candidature?.length || 0) === 0 ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Briefcase className="w-12 h-12 text-slate-300 mb-4" />
                <p className="text-slate-500">Nessuna candidatura inviata</p>
                <p className="text-sm text-slate-400">Candidati ai concorsi per vedere le tue domande qui</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {riepilogo?.lavoro?.candidature?.map((cand) => (
                <Card key={cand.id} className="hover:shadow-md transition-shadow">
                  <CardHeader className="pb-2">
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-base">{cand.titolo_concorso}</CardTitle>
                        <CardDescription className="flex items-center gap-1">
                          <Building className="w-3 h-3" />
                          {cand.azienda_nome || 'Azienda non specificata'}
                        </CardDescription>
                      </div>
                      {getStatusBadge(cand.stato)}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-sm text-slate-600">
                      <p className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-slate-400" />
                        Inviata: {new Date(cand.created_at).toLocaleDateString('it-IT')}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ASSUNZIONI - STORICO */}
        <TabsContent value="assunzioni" className="mt-6 space-y-4">
          {/* Impieghi Attuali */}
          <div>
            <h3 className="text-lg font-semibold mb-3">Impieghi Attuali</h3>
            {(riepilogo?.lavoro?.impieghi_attuali?.length || 0) === 0 ? (
              <Card className="border-dashed">
                <CardContent className="flex flex-col items-center justify-center py-8">
                  <Briefcase className="w-10 h-10 text-slate-300 mb-2" />
                  <p className="text-slate-500">Nessun impiego attivo</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-3 md:grid-cols-2">
                {riepilogo?.lavoro?.impieghi_attuali?.map((imp, idx) => (
                  <Card key={idx} className="bg-green-50 border-green-200">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                          <Building className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <p className="font-medium text-green-900">{imp.azienda_nome}</p>
                          <p className="text-sm text-green-700">{imp.ruolo_nome || 'Dipendente'}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* Storico Assunzioni/Licenziamenti */}
          <div>
            <h3 className="text-lg font-semibold mb-3">Storico Assunzioni e Licenziamenti</h3>
            {(riepilogo?.lavoro?.storico_assunzioni?.length || 0) === 0 ? (
              <Card className="border-dashed">
                <CardContent className="flex flex-col items-center justify-center py-8">
                  <History className="w-10 h-10 text-slate-300 mb-2" />
                  <p className="text-slate-500">Nessuno storico disponibile</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {riepilogo?.lavoro?.storico_assunzioni?.map((entry) => (
                  <Card key={entry.id} className={entry.tipo === 'ASSUNZIONE' ? 'border-l-4 border-l-green-500' : 'border-l-4 border-l-red-500'}>
                    <CardContent className="p-4">
                      <div className="flex items-start gap-4">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${entry.tipo === 'ASSUNZIONE' ? 'bg-green-100' : 'bg-red-100'}`}>
                          {entry.tipo === 'ASSUNZIONE' ? (
                            <UserPlus className={`w-5 h-5 text-green-600`} />
                          ) : (
                            <UserMinus className={`w-5 h-5 text-red-600`} />
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Badge className={entry.tipo === 'ASSUNZIONE' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                              {entry.tipo === 'ASSUNZIONE' ? 'Assunto' : 'Licenziato'}
                            </Badge>
                            <span className="font-medium">{entry.azienda_nome}</span>
                          </div>
                          <p className="text-sm text-slate-600 mt-1">
                            Ruolo: {entry.ruolo_nome || 'N/A'}
                          </p>
                          {entry.motivo && (
                            <p className="text-sm text-red-600 mt-1 font-medium">
                              Motivo: {entry.motivo}
                            </p>
                          )}
                          <div className="flex items-center gap-2 mt-2 text-xs text-slate-400">
                            <User className="w-3 h-3" />
                            <span>Da: <span className="text-slate-600 font-medium">@{entry.operatore_username || entry.operatore_nome}</span></span>
                            <span>•</span>
                            <Calendar className="w-3 h-3" />
                            <span>{new Date(entry.created_at).toLocaleDateString('it-IT')}</span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Profilo;
